import type { QueryClient } from '@tanstack/react-query';
import { getSessionStateServer, type UserSession } from '@/lib/server-fns/auth-fns';
import { toRootAuthData, type RootAuthData } from '@/lib/server-fns/rootAuthState';
import type { SessionLoadResult } from '@/lib/server-fns/sessionLoader';
import { logger } from '@/lib/utils/logger';

export const AUTH_SESSION_QUERY_KEY = ['auth', 'session'] as const;
// Committed navigations reuse the hydrated session for this long before
// re-validating through the server-fn round trip. Revocation is handled
// reactively (QueryCache 401 purge → purgeAuthState), so widening the window
// trades only worst-case revocation latency for navigation latency: at 60s
// nearly every dwell exceeded it, serializing a session RPC ahead of every
// route loader (the router resolves root beforeLoad before child loaders).
const AUTH_SESSION_NAVIGATION_FRESH_MS = 10 * 60_000;

export type RootRouterContext = {
  queryClient: QueryClient;
};

type SessionAuthority = () => Promise<SessionLoadResult<UserSession>>;

export function shouldReuseCachedRootSession(
  queryState: { data: unknown; dataUpdatedAt: number } | undefined,
  now = Date.now()
): boolean {
  return (
    queryState?.data !== undefined &&
    queryState.dataUpdatedAt > 0 &&
    now - queryState.dataUpdatedAt < AUTH_SESSION_NAVIGATION_FRESH_MS
  );
}

function readCachedRootAuthData(context: RootRouterContext): RootAuthData<UserSession> {
  const queryState = context.queryClient.getQueryState<UserSession | null>(AUTH_SESSION_QUERY_KEY);
  if (!queryState || queryState.data === undefined) {
    return { authState: 'unavailable', session: null };
  }
  if (queryState.data === null) {
    return { authState: 'unauthenticated', session: null };
  }
  return { authState: 'authenticated', session: queryState.data };
}

export function loadRootSessionContextForMatch(
  context: RootRouterContext,
  preload: boolean,
  loadSession: SessionAuthority = getSessionStateServer
): Promise<RootAuthData<UserSession>> {
  // Intent preloads must not validate or mutate the active session. The
  // committed navigation runs beforeLoad again and becomes the sole authority.
  if (preload) {
    return Promise.resolve(readCachedRootAuthData(context));
  }
  // The document load always validates cookies on the server. Once hydrated,
  // tab navigation reuses that authority briefly; token refresh and logout
  // explicitly update or clear this cache, so they bypass the window.
  if (
    typeof window !== 'undefined' &&
    shouldReuseCachedRootSession(context.queryClient.getQueryState(AUTH_SESSION_QUERY_KEY))
  ) {
    return Promise.resolve(readCachedRootAuthData(context));
  }
  // Past the fresh window we still have a session — revalidating it is
  // defensive, NOT critical for rendering this navigation. Awaiting the RPC
  // here serializes a server round trip ahead of every route loader (the
  // router resolves root beforeLoad first), which is exactly the blocking we
  // must avoid: render from the cached session and heal it in the background.
  // Revocation stays reactive (a 401 purges auth state), so the worst case is
  // one navigation rendering from a session that expired seconds ago.
  const cached = readCachedRootAuthData(context);
  if (typeof window !== 'undefined' && cached.authState !== 'unavailable') {
    void loadRootSessionContext(context, loadSession).catch(() => undefined);
    return Promise.resolve(cached);
  }
  return loadRootSessionContext(context, loadSession);
}

export async function loadRootSessionContext(
  context: RootRouterContext,
  loadSession: SessionAuthority = getSessionStateServer
): Promise<RootAuthData<UserSession>> {
  try {
    logger.info('RootBeforeLoad', 'Fetching session server-side');
    const authData = toRootAuthData(await loadSession());

    if (authData.authState === 'unavailable') {
      context.queryClient.removeQueries({
        queryKey: AUTH_SESSION_QUERY_KEY,
        exact: true,
      });
    } else {
      context.queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, authData.session);
    }

    logger.info('RootBeforeLoad', 'Session fetch complete', {
      authState: authData.authState,
      hasSession: !!authData.session,
    });
    return authData;
  } catch (error) {
    context.queryClient.removeQueries({
      queryKey: AUTH_SESSION_QUERY_KEY,
      exact: true,
    });
    logger.error('RootBeforeLoad', 'Session fetch failed', { error });
    return { authState: 'unavailable', session: null };
  }
}
